import { type ReactNode, useCallback, useMemo } from "react";

import { useToast } from "@hightouch/htui";
import { type FileRejection, useDropzone } from "react-dropzone";
import type { AttachmentUpload } from "src/components/llm/context/file-context";
import { useComposerFileStore } from "src/components/llm/context/use-composer-file-store";
import {
  type ComposerFile,
  extractFilesFromClipboardEvent,
  isImage,
} from "src/components/llm/utils/file-processing";
import { usePageDropZoneActive } from "./page-file-drop-zone";
import { getFileSrc } from "./file-preview-card";
import {
  useImageGalleryOverlay,
  type GalleryImage,
} from "./full-screen-image-overlay";

export type ComposerAttachmentsOptions = {
  /**
   * When false the composer takes no attachments at all — picker, paste and
   * drop all go inert. Mirrors `fileConfig.enableFileUpload`.
   * @default true
   */
  isEnabled?: boolean;
  /** The composer is disabled outright. */
  isDisabled?: boolean;
  /** An agent turn is in flight. */
  isLoading?: boolean;
};

export type ComposerAttachments = {
  files: ComposerFile[];
  setFiles: (
    files: ComposerFile[] | ((prev: ComposerFile[]) => ComposerFile[]),
  ) => void;
  /** Raw `File`s from the picker, a paste, or a drop. Encodes then appends. */
  addRawFiles: (rawFiles: File[]) => Promise<void>;
  /** Already-encoded files from an attachment source (content library, …). */
  addFileInputs: (newFiles: ComposerFile[]) => void;
  removeFileAt: (index: number) => void;
  clearFiles: () => void;
  /** True while any attachment is still being encoded. */
  isProcessing: boolean;
  /** Attachments still uploading, keyed by file name. */
  uploadsInFlight: Map<string, AttachmentUpload>;
  removeAttachment: (id: string) => void;
  removeAttachments: (ids: string[]) => void;
  retryAttachment: (id: string) => void;
  /** True when a message must not be sent yet (a turn is in flight, or an
   * attachment is still encoding and would be left out of the payload). */
  isBusy: boolean;
  handlePaste: (event: React.ClipboardEvent) => void;
  /** Spread onto the element that should accept drops. Empty when suppressed. */
  dropzoneRootProps: Record<string, unknown>;
  isDragActive: boolean;
  galleryIndexFor: (file: ComposerFile) => number | undefined;
  openGallery: (index: number) => void;
  galleryOverlay: ReactNode;
};

/**
 * Everything a chat composer needs to accept attachments: storage, the cap and
 * busy policy, the four input paths (picker, paste, drop, attachment sources),
 * and the fullscreen gallery.
 *
 * This exists because the composers used to implement all of it twice, and the
 * copies drifted — one capped attachments and one didn't, one toasted on an
 * unsupported paste and one dropped it silently, one had drag-and-drop and one
 * relied on the page to provide it. Anything that belongs to "attaching a file
 * to a message" belongs here, so a fix lands on every composer at once.
 */
export const useComposerAttachments = ({
  isEnabled = true,
  isDisabled = false,
  isLoading = false,
}: ComposerAttachmentsOptions = {}): ComposerAttachments => {
  const store = useComposerFileStore();
  const { toast } = useToast();
  // A PageFileDropZone above us already writes into the same store; running a
  // second dropzone here would attach every dropped file twice.
  const isPageDropZoneActive = usePageDropZoneActive();

  const {
    files,
    setFiles,
    addProcessedFiles,
    isProcessingFiles,
    uploadsInFlight,
  } = store;

  // Staging an attachment mid-turn is allowed: submit is already gated while a
  // turn is in flight, so the file just waits for the next message. Only a
  // composer that can't hold attachments at all refuses them.
  const isBlocked = !isEnabled || isDisabled;
  const isBusy = isLoading || isProcessingFiles;

  const addRawFiles = useCallback(
    async (rawFiles: File[]) => {
      if (isBlocked || rawFiles.length === 0) return;
      await addProcessedFiles(rawFiles);
    },
    [isBlocked, addProcessedFiles],
  );

  const addFileInputs = (newFiles: ComposerFile[]) => {
    if (isBlocked || newFiles.length === 0) return;
    void store.addAttachments(newFiles);
  };

  // By index, not by fileName: nothing dedupes attachments, so two files can
  // share a name and removing by name would take both.
  const removeFileAt = useCallback(
    (index: number) => {
      setFiles((prev) => prev.filter((_, i) => i !== index));
    },
    [setFiles],
  );

  const clearFiles = useCallback(() => setFiles([]), [setFiles]);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      if (isBlocked) return;

      const { files: pastedFiles, rejectedMimeTypes } =
        extractFilesFromClipboardEvent(event);

      if (rejectedMimeTypes.length > 0) {
        toast({
          id: "paste-image-unsupported",
          variant: "warning",
          title: "Unsupported image format",
          message: `Paste isn't supported for ${rejectedMimeTypes.join(
            ", ",
          )}. Re-save as PNG or JPEG and try again.`,
        });
      }

      // Nothing on the clipboard we can attach — let the browser paste the text.
      if (pastedFiles.length === 0) return;
      event.preventDefault();
      void addRawFiles(pastedFiles);
    },
    [isBlocked, toast, addRawFiles],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // No `accept` filter is set, so rejections shouldn't occur — but if the
      // library rejects anyway, say so instead of dropping the file silently.
      if (fileRejections.length > 0) {
        const names = fileRejections.map((r) => r.file.name).join(", ");
        toast({
          id: "drop-file-unsupported",
          variant: "warning",
          title: "Unsupported file",
          message: `Couldn't attach ${names}.`,
        });
      }
      if (acceptedFiles.length > 0) void addRawFiles(acceptedFiles);
    },
    [addRawFiles, toast],
  );

  // react-dropzone's file-selector walks dropped directories into a flat File
  // list, keeping each file's relative path, which the store reads as its folder.
  // Any file type is accepted here; the blocked-extension denylist is enforced
  // by both processing paths, which every attachment path funnels through.
  const { getRootProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    disabled: isBlocked || isPageDropZoneActive,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const { galleryImages, galleryIndexByFile } = useMemo(() => {
    const images: GalleryImage[] = [];
    const indexByFile = new Map<ComposerFile, number>();
    for (const file of files) {
      if (!isImage(file.mimeType)) continue;
      const src = getFileSrc(file);
      if (!src) continue;
      indexByFile.set(file, images.length);
      images.push({ imageSrc: src, fileName: file.fileName });
    }
    return { galleryImages: images, galleryIndexByFile: indexByFile };
  }, [files]);

  const { openGallery, overlay: galleryOverlay } =
    useImageGalleryOverlay(galleryImages);

  const galleryIndexFor = useCallback(
    (file: ComposerFile) => galleryIndexByFile.get(file),
    [galleryIndexByFile],
  );

  return {
    files,
    setFiles,
    addRawFiles,
    addFileInputs,
    removeFileAt,
    clearFiles,
    isProcessing: isProcessingFiles,
    uploadsInFlight,
    removeAttachment: store.removeAttachment,
    removeAttachments: store.removeAttachments,
    retryAttachment: store.retryAttachment,
    isBusy,
    handlePaste,
    dropzoneRootProps: isPageDropZoneActive ? EMPTY_ROOT_PROPS : getRootProps(),
    isDragActive,
    galleryIndexFor,
    openGallery,
    galleryOverlay,
  };
};

/** Stable identity so spreading it doesn't churn the host element's props. */
const EMPTY_ROOT_PROPS: Record<string, unknown> = {};
